import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { normalizeThreadSummaryAttachments } from "@/entities/thread/normalizeAttachments";
import type { ThreadSummary } from "@/entities/thread/types";
import {
  type HistoryThreadRef,
  historyThreadKey,
} from "@/features/history/historyIdentity";
import {
  type HistorySummaryScope,
  selectHistoryItemsForScope,
} from "@/features/history/hooks/historySummaryScope";
import { apiGet } from "@/shared/api/client";

const THREAD_BATCH_LIMIT = 20;

export function createThreadIdBatches(ids: readonly string[]): string[][] {
  const batches: string[][] = [];
  for (let offset = 0; offset < ids.length; offset += THREAD_BATCH_LIMIT) {
    batches.push(ids.slice(offset, offset + THREAD_BATCH_LIMIT));
  }
  return batches;
}

type SummaryFetcher = <T>(
  path: string,
  options?: { signal?: AbortSignal; boardKey?: string },
) => Promise<T>;

async function fetchBoardSummaries(
  slug: string,
  ids: readonly string[],
  signal: AbortSignal | undefined,
  fetcher: SummaryFetcher,
): Promise<ThreadSummary[]> {
  const results = await Promise.all(
    createThreadIdBatches(ids).map((batch) =>
      // path slugと明示的なboardKeyを取得対象の板へ揃える。
      fetcher<ThreadSummary[]>(
        `/boards/${encodeURIComponent(slug)}/summary?ids=${batch.map(encodeURIComponent).join(",")}`,
        { signal, boardKey: slug },
      ),
    ),
  );
  return results.flat();
}

/**
 * 履歴を保存元の板ごとに分け、scope内の板からスレ概要を取得する。
 * 板とIDの組で照合する。未登録板など単一板の失敗は
 * その板抜きで縮退し、合算全体は失敗させない(単板scopeでは従来どおり失敗)。
 *
 * 戻り値は要求した履歴の順(履歴ストアの新しい順)に揃える。板ごとの取得順の
 * まま返すと、合算時に板単位でまとまってしまい閲覧順が崩れるため。
 */
export async function fetchHistoryThreadSummaries(
  scope: HistorySummaryScope,
  items: readonly HistoryThreadRef[],
  signal?: AbortSignal,
  fetcher: SummaryFetcher = apiGet,
): Promise<ThreadSummary[]> {
  const scopedItems = selectHistoryItemsForScope(items, scope);
  const byBoard = new Map<string, Set<string>>();
  for (const item of scopedItems) {
    const ids = byBoard.get(item.boardSlug) ?? new Set<string>();
    ids.add(item.threadId);
    byBoard.set(item.boardSlug, ids);
  }
  const byId = new Map<string, ThreadSummary>();
  await Promise.all(
    [...byBoard].map(async ([slug, ids]) => {
      try {
        const threads = await fetchBoardSummaries(
          slug,
          [...ids],
          signal,
          fetcher,
        );
        for (const thread of threads) {
          if (ids.has(thread.id)) {
            byId.set(
              historyThreadKey({ boardSlug: slug, threadId: thread.id }),
              scope.tagBoard ? { ...thread, b: slug } : thread,
            );
          }
        }
      } catch (error) {
        if (
          scope.slugs.length === 1 ||
          (error instanceof Error && error.name === "AbortError")
        )
          throw error;
      }
    }),
  );

  const seen = new Set<string>();
  return scopedItems.flatMap((item) => {
    const key = historyThreadKey(item);
    if (seen.has(key)) return [];
    seen.add(key);
    const thread = byId.get(key);
    return thread ? [normalizeThreadSummaryAttachments(thread)] : [];
  });
}

export function useHistoryThreads(
  mode: "viewed" | "posted",
  items: readonly HistoryThreadRef[],
  scope: HistorySummaryScope,
): UseQueryResult<ThreadSummary[]> {
  const refs = selectHistoryItemsForScope(items, scope).map(
    ({ boardSlug, threadId }) => ({ boardSlug, threadId }),
  );
  return useQuery({
    queryKey: ["catalog-history", scope.slugs, scope.tagBoard, mode, refs],
    queryFn: async ({
      signal,
    }: {
      signal: AbortSignal;
    }): Promise<ThreadSummary[]> =>
      fetchHistoryThreadSummaries(scope, refs, signal),
    enabled: refs.length > 0,
    staleTime: 30_000,
  });
}
